import * as React from "react";
import * as Controls from "VSS/Controls";

import { MarkdownSyntax } from "Discussion/Scripts/CommonConstants";
import { AutocompleteEnhancement } from "Mention/Scripts/TFS.Mention.Autocomplete.Controls";
import * as CommonMarkdownToolbar from "ContentRendering/MarkdownToolbar/MarkdownToolbar"
import { MarkdownToolbarHelper } from "ContentRendering/MarkdownToolbar/Utility"
import * as MentionResources from "Mention/Scripts/Resources/TFS.Resources.Mention";

import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";

export enum MentionCommandType {
    People,
    WorkItem,
    PullRequest,
}

/**
This provides MarkdownToolbar commands to add mentions and WIT.
Will be used to extend the common MarkdownToolbar
 */
export class MarkdownToolbarMentionCommands {
    private getTextArea: () => HTMLTextAreaElement;
    private textChanged: (text: string, selectionStart: number, selectionEnd: number) => void;

    constructor(getTextArea: () => HTMLTextAreaElement,
        textChanged: (text: string, selectionStart: number, selectionEnd: number) => void) {
        this.getTextArea = getTextArea;
        this.textChanged = textChanged;
    }

    public getCommands(commandTypes: MentionCommandType[], mentionGroupsEnabled?: boolean, menuItemClickedCallback?: (menuItemKey: string) => void): CommonMarkdownToolbar.MarkdownToolbarCommand[] {
        let buttonClass = "markdowntoolbar-button";
        let insertLocation = 8;
        let commands = [];
        const mentionMenuItemKey = "mention-menu-item";
        const witMenuItemKey = "wit-menu-item";
        const PRMenuItemKey = "pr-menu-item";
        const atMentionString = mentionGroupsEnabled ? MentionResources.AtMention : MentionResources.MentionSomeone;

        for (const commandType of commandTypes) {
            switch (commandType) {
                case MentionCommandType.People:
                    commands.push({
                        index: insertLocation++,
                        key: mentionMenuItemKey,
                        name: atMentionString,
                        className: buttonClass,
                        icon: "Accounts",
                        title: atMentionString,
                        onClick: () => { this._insertMention(MarkdownSyntax.Mention, mentionMenuItemKey, menuItemClickedCallback) },
                        ariaLabel: atMentionString
                    });
                    break;
                case MentionCommandType.WorkItem:
                    commands.push({
                        index: insertLocation++,
                        key: witMenuItemKey,
                        name: MentionResources.MentionWorkItem,
                        className: buttonClass,
                        icon: "NumberSymbol",
                        title: MentionResources.MentionWorkItem,
                        onClick: () => { this._insertMention(MarkdownSyntax.LinkWIT, witMenuItemKey, menuItemClickedCallback) },
                        ariaLabel: MentionResources.MentionWorkItem
                    });
                    break;
                case MentionCommandType.PullRequest:
                    if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.WebAccessVersionControlPullRequestsMentions, false)) {
                        commands.push({
                            index: insertLocation++,
                            key: PRMenuItemKey,
                            name: MentionResources.MentionPR,
                            className: buttonClass,
                            icon: "BranchPullRequest",
                            title: MentionResources.MentionPR,
                            onClick: () => { this._insertMention(MarkdownSyntax.LinkPR, PRMenuItemKey, menuItemClickedCallback) },
                            ariaLabel: MentionResources.MentionPR
                        });
                    }
                    break;
            }
        }

        return commands;
    }

    private _insertMention = (insertedString: string, menuItemKey: string, menuItemClickedCallback?: (menuItemKey: string) => void): void => {
        let textArea = this.getTextArea();
        if (!textArea) {
            return;
        }

        let currentText = textArea.value;
        let selectionStart = textArea.selectionStart;
        let selectionEnd = textArea.selectionEnd;
        let newText = insertedString;

        // The preceding character needs to be whitespace
        if (selectionStart > 0 && !(/\s/.test(currentText[selectionStart - 1]))) {
            newText = " " + newText;
        }

        textArea.focus();
        if (MarkdownToolbarHelper.browserSupportsInsertText()) {
            // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
            // This will also trigger any events on the parent control as if the user typed in this text
            document.execCommand("insertText", false, newText + (""));
            this.textChanged(null, textArea.selectionStart, textArea.selectionEnd);
        }
        else {
            let firstPart = currentText.substring(0, selectionStart);
            let endPart = currentText.substring(selectionEnd);
            let newCursorPosition = firstPart.length + newText.length;

            // otherwise, the parent control needs to deal with the new text
            newText += "";
            this.textChanged(firstPart + newText + endPart, newCursorPosition, newCursorPosition);

            let enhancement = Controls.Enhancement.getInstance(AutocompleteEnhancement, $(textArea)) as AutocompleteEnhancement;
            if (enhancement) {
                enhancement.runAutocomplete({
                    textBeforeSelection: firstPart + newText,
                    textInSelection: "",
                    textAfterSelection: endPart
                });
            }
        }

        menuItemClickedCallback && menuItemClickedCallback(menuItemKey);
    }
}
